// src/components/SmartTags.jsx
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Tag,
  Plus,
  X,
  Check,
  Sparkles,
  TrendingUp,
  Clock,
  Star,
  AlertCircle,
  Heart,
  Home,
  DollarSign,
  MapPin,
  Calendar,
  Users,
  Zap,
  Award,
  Flag,
  Bookmark,
  Filter,
  ChevronDown,
  ChevronUp,
  Edit2,
  Trash2,
  Save
} from 'lucide-react';
import { useAuth } from '../contexts/useAuth';
import { useNotifications } from '../contexts/NotificationsContext';
import { useIsMobile } from '../hooks/useMediaQuery';

/**
 * 🚀 SmartTags - VERSÃO DEFINITIVA
 * 📱 ADAPTADA PARA DISPOSITIVOS MÓVILES
 * 
 * ✅ SEM LOOPS INFINITOS
 * ✅ SEM CONGELAMENTOS
 * ✅ ULTRA RÁPIDO
 * ✅ 100% ESTÁVEL
 * ✅ TODAS AS KEYS CORRIGIDAS
 */

// ==========================================================
// CONSTANTES (IMUTÁVEIS)
// ==========================================================
const PREDEFINED_TAGS = [
  { id: 'comprador', label: 'Comprador', icon: 'user', category: 'perfil', color: 'blue' },
  { id: 'vendedor', label: 'Vendedor', icon: 'user', category: 'perfil', color: 'green' },
  { id: 'locatario', label: 'Locatário', icon: 'user', category: 'perfil', color: 'purple' },
  { id: 'investidor', label: 'Investidor', icon: 'trending-up', category: 'perfil', color: 'amber' },
  { id: 'apartamento', label: 'Apartamento', icon: 'home', category: 'interesse', color: 'indigo' },
  { id: 'casa', label: 'Casa', icon: 'home', category: 'interesse', color: 'emerald' },
  { id: 'comercial', label: 'Comercial', icon: 'home', category: 'interesse', color: 'orange' },
  { id: 'terreno', label: 'Terreno', icon: 'map-pin', category: 'interesse', color: 'lime' },
  { id: 'ate-200k', label: 'Até R$ 200k', icon: 'dollar-sign', category: 'orcamento', color: 'teal' },
  { id: '200k-500k', label: 'R$ 200k - 500k', icon: 'dollar-sign', category: 'orcamento', color: 'cyan' },
  { id: '500k-1m', label: 'R$ 500k - 1M', icon: 'dollar-sign', category: 'orcamento', color: 'sky' },
  { id: 'acima-1m', label: 'Acima de R$ 1M', icon: 'dollar-sign', category: 'orcamento', color: 'blue' },
  { id: 'quente', label: 'Quente 🔥', icon: 'zap', category: 'prioridade', color: 'red' },
  { id: 'morno', label: 'Morno', icon: 'clock', category: 'prioridade', color: 'amber' },
  { id: 'frio', label: 'Frio', icon: 'snow', category: 'prioridade', color: 'blue' },
  { id: 'urgente', label: 'Urgente ⚡', icon: 'alert-circle', category: 'prioridade', color: 'red' },
  { id: 'novo', label: 'Novo', icon: 'star', category: 'status', color: 'green' },
  { id: 'contatado', label: 'Contatado', icon: 'check', category: 'status', color: 'blue' },
  { id: 'reuniao', label: 'Reunião agendada', icon: 'calendar', category: 'status', color: 'purple' },
  { id: 'proposta', label: 'Proposta enviada', icon: 'file', category: 'status', color: 'orange' },
  { id: 'negociacao', label: 'Em negociação', icon: 'trending-up', category: 'status', color: 'amber' },
  { id: 'fechado', label: 'Fechado ✅', icon: 'award', category: 'status', color: 'emerald' },
  { id: 'perdido', label: 'Perdido ❌', icon: 'x', category: 'status', color: 'slate' },
  { id: 'financiamento', label: 'Financiamento', icon: 'dollar-sign', category: 'caracteristicas', color: 'teal' },
  { id: 'consorcio', label: 'Consórcio', icon: 'users', category: 'caracteristicas', color: 'indigo' },
  { id: 'primeiro-imovel', label: 'Primeiro imóvel', icon: 'heart', category: 'caracteristicas', color: 'rose' },
  { id: 'trocar-imovel', label: 'Trocar de imóvel', icon: 'refresh', category: 'caracteristicas', color: 'cyan' },
  { id: 'zona-norte', label: 'Zona Norte', icon: 'map-pin', category: 'localizacao', color: 'sky' },
  { id: 'zona-sul', label: 'Zona Sul', icon: 'map-pin', category: 'localizacao', color: 'emerald' },
  { id: 'zona-leste', label: 'Zona Leste', icon: 'map-pin', category: 'localizacao', color: 'amber' },
  { id: 'zona-oeste', label: 'Zona Oeste', icon: 'map-pin', category: 'localizacao', color: 'purple' },
  { id: 'centro', label: 'Centro', icon: 'map-pin', category: 'localizacao', color: 'red' }
];

const ICON_MAP = {
  'user': Users, 'trending-up': TrendingUp, 'home': Home, 'map-pin': MapPin,
  'dollar-sign': DollarSign, 'zap': Zap, 'clock': Clock, 'alert-circle': AlertCircle,
  'star': Star, 'check': Check, 'calendar': Calendar, 'file': Bookmark,
  'award': Award, 'x': X, 'heart': Heart, 'users': Users,
  'refresh': Clock, 'flag': Flag, 'default': Tag
};

const CATEGORY_COLORS = {
  perfil: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', hover: 'hover:bg-blue-200' },
  interesse: { bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-200', hover: 'hover:bg-indigo-200' },
  orcamento: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', hover: 'hover:bg-emerald-200' },
  prioridade: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', hover: 'hover:bg-amber-200' },
  status: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200', hover: 'hover:bg-purple-200' },
  caracteristicas: { bg: 'bg-rose-100', text: 'text-rose-700', border: 'border-rose-200', hover: 'hover:bg-rose-200' },
  localizacao: { bg: 'bg-cyan-100', text: 'text-cyan-700', border: 'border-cyan-200', hover: 'hover:bg-cyan-200' },
  personalizada: { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', hover: 'hover:bg-slate-200' }
};

const DEFAULT_COLOR = { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', hover: 'hover:bg-slate-200' };

// ==========================================================
// FUNÇÕES PURAS
// ==========================================================
const getTagColor = (tag) => (tag?.category && CATEGORY_COLORS[tag.category]) || DEFAULT_COLOR;

const getTagIcon = (tag) => {
  const IconComponent = ICON_MAP[tag?.icon] || ICON_MAP.default;
  return <IconComponent className={`${tag?.iconSize || 'w-3 h-3'}`} />;
};

const generateSuggestions = (client) => {
  if (!client) return [];
  
  const suggestions = [];
  const tagMap = new Map();

  if (client.score >= 80) tagMap.set('quente', PREDEFINED_TAGS.find(t => t.id === 'quente'));
  else if (client.score >= 50) tagMap.set('morno', PREDEFINED_TAGS.find(t => t.id === 'morno'));
  else tagMap.set('frio', PREDEFINED_TAGS.find(t => t.id === 'frio'));

  if (client.type === 'Comprador') tagMap.set('comprador', PREDEFINED_TAGS.find(t => t.id === 'comprador'));
  else if (client.type === 'Vendedor') tagMap.set('vendedor', PREDEFINED_TAGS.find(t => t.id === 'vendedor'));
  else if (client.type === 'Investidor') tagMap.set('investidor', PREDEFINED_TAGS.find(t => t.id === 'investidor'));

  if (client.budget) {
    const budget = parseFloat(client.budget);
    if (budget < 200000) tagMap.set('ate-200k', PREDEFINED_TAGS.find(t => t.id === 'ate-200k'));
    else if (budget < 500000) tagMap.set('200k-500k', PREDEFINED_TAGS.find(t => t.id === '200k-500k'));
    else if (budget < 1000000) tagMap.set('500k-1m', PREDEFINED_TAGS.find(t => t.id === '500k-1m'));
    else tagMap.set('acima-1m', PREDEFINED_TAGS.find(t => t.id === 'acima-1m'));
  }

  if (client.pipeline === 'Lead Novo') tagMap.set('novo', PREDEFINED_TAGS.find(t => t.id === 'novo'));
  else if (client.pipeline === 'Proposta Enviada') tagMap.set('proposta', PREDEFINED_TAGS.find(t => t.id === 'proposta'));
  else if (client.pipeline === 'Fechado') tagMap.set('fechado', PREDEFINED_TAGS.find(t => t.id === 'fechado'));

  return Array.from(tagMap.values()).filter(Boolean);
};

// ==========================================================
// COMPONENTE PRINCIPAL
// ==========================================================
export const SmartTags = ({
  clientId,
  tags: externalTags = [],
  onTagsChange,
  readOnly = false,
  showSuggestions = true,
  showCategories = true,
  showSearch = true,
  allowCustom = true,
  maxTags = 20,
  size = 'md',
  className = ''
}) => {
  // ==========================================================
  // HOOKS BÁSICOS
  // ==========================================================
  const { database } = useAuth();
  const isMobile = useIsMobile();
  const notificationsCtx = (() => {
    try { return useNotifications(); } catch { return null; }
  })();

  const notify = notificationsCtx?.notifySystem ||
    notificationsCtx?.notify ||
    ((msg) => console.log('🏷️ Tags:', msg));

  // ==========================================================
  // ESTADOS LOCAIS
  // ==========================================================
  const [currentTags, setCurrentTags] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllTags, setShowAllTags] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [customTagInput, setCustomTagInput] = useState('');
  const [editingTag, setEditingTag] = useState(null);
  
  // ==========================================================
  // REFS
  // ==========================================================
  const searchInputRef = useRef(null);
  const customInputRef = useRef(null);
  const isMounted = useRef(true);
  const suggestionsTimeoutRef = useRef(null);
  const initialLoadDone = useRef(false);
  const prevClientDataRef = useRef({ id: null, tagsHash: '' });

  // ==========================================================
  // CLEANUP
  // ==========================================================
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (suggestionsTimeoutRef.current) clearTimeout(suggestionsTimeoutRef.current);
    };
  }, []);

  // ==========================================================
  // 🟢 CARREGAR TAGS INICIAIS - CORRIGIDO (UMA VEZ SÓ)
  // ==========================================================
  useEffect(() => {
    if (initialLoadDone.current) return;

    if (externalTags.length > 0) {
      setCurrentTags(externalTags);
      initialLoadDone.current = true;
    } else if (clientId && database) {
      try {
        const client = database.getClientById(clientId);
        if (client?.tags && Array.isArray(client.tags)) {
          setCurrentTags(client.tags);
          initialLoadDone.current = true;
        }
      } catch (error) {
        console.error('❌ Erro ao carregar tags:', error);
      }
    }
  }, [clientId, database, externalTags]);

  // ==========================================================
  // 🟢 ATUALIZAR QUANDO EXTERNAL TAGS MUDAR (CASO RARO)
  // ==========================================================
  useEffect(() => {
    if (initialLoadDone.current && externalTags.length > 0) {
      setCurrentTags(externalTags);
    }
  }, [externalTags]);

  // ==========================================================
  // 🏆 GERAR SUGESTÕES - ULTRA OTIMIZADO
  // ==========================================================
  useEffect(() => {
    if (!showSuggestions || !clientId || !database) {
      setSuggestions([]);
      return;
    }

    let client;
    try {
      client = database.getClientById(clientId);
    } catch {
      setSuggestions([]);
      return;
    }

    if (!client) {
      setSuggestions([]);
      return;
    }

    const currentTagsHash = JSON.stringify(client.tags || []);
    if (prevClientDataRef.current.id === clientId && 
        prevClientDataRef.current.tagsHash === currentTagsHash) {
      return;
    }

    prevClientDataRef.current = { id: clientId, tagsHash: currentTagsHash };

    if (suggestionsTimeoutRef.current) clearTimeout(suggestionsTimeoutRef.current);

    suggestionsTimeoutRef.current = setTimeout(() => {
      if (!isMounted.current) return;

      try {
        setSuggestions(generateSuggestions(client));
      } catch (error) {
        console.error('❌ Erro ao gerar sugestões:', error);
        setSuggestions([]);
      }
    }, 150);

    return () => {
      if (suggestionsTimeoutRef.current) clearTimeout(suggestionsTimeoutRef.current);
    };
  }, [clientId, database, showSuggestions]);

  // ==========================================================
  // FILTROS MEMOIZADOS
  // ==========================================================
  const filteredPredefinedTags = useMemo(() => {
    let filtered = PREDEFINED_TAGS;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.label.toLowerCase().includes(term) ||
        t.category?.toLowerCase().includes(term)
      );
    }

    const currentTagIds = new Set(currentTags.map(t => t.id));
    const currentTagLabels = new Set(currentTags.map(t => t.label?.toLowerCase()));
    
    filtered = filtered.filter(t => 
      !currentTagIds.has(t.id) && 
      !currentTagLabels.has(t.label.toLowerCase())
    );

    return filtered;
  }, [selectedCategory, searchTerm, currentTags]);

  const categories = useMemo(() => {
    const cats = ['all', ...new Set(PREDEFINED_TAGS.map(t => t.category))];
    return cats.map(cat => ({
      id: cat,
      label: cat === 'all' ? 'Todas' : cat.charAt(0).toUpperCase() + cat.slice(1)
    }));
  }, []);

  // ==========================================================
  // HANDLERS (MEMOIZADOS)
  // ==========================================================
  const handleAddTag = useCallback((tag) => {
    if (currentTags.length >= maxTags) {
      notify({ type: 'warning', title: 'Limite atingido', message: `Máximo de ${maxTags} tags permitidas.` });
      return;
    }

    const newTag = typeof tag === 'string' ? {
      id: `custom_${Date.now()}`,
      label: tag,
      category: 'personalizada',
      icon: 'default',
      custom: true
    } : tag;

    const updatedTags = [...currentTags, newTag];

    setCurrentTags(updatedTags);
    if (onTagsChange) onTagsChange(updatedTags);

    if (clientId && database?.updateClient) {
      try {
        const client = database.getClientById(clientId);
        database.updateClient(clientId, { ...client, tags: updatedTags });
      } catch (error) {
        console.error('❌ Erro ao salvar tags:', error);
      }
    }

    setSearchTerm('');
    setCustomTagInput('');
    notify({ type: 'success', title: 'Tag adicionada', message: `"${newTag.label}" foi adicionada.` });
  }, [currentTags, maxTags, clientId, database, onTagsChange, notify]);

  const handleRemoveTag = useCallback((tagToRemove) => {
    const updatedTags = currentTags.filter(t => t.id !== tagToRemove.id && t.label !== tagToRemove.label);

    setCurrentTags(updatedTags);
    if (onTagsChange) onTagsChange(updatedTags);

    if (clientId && database?.updateClient) {
      try {
        const client = database.getClientById(clientId);
        database.updateClient(clientId, { ...client, tags: updatedTags });
      } catch (error) {
        console.error('❌ Erro ao salvar tags:', error);
      }
    }

    notify({ type: 'success', title: 'Tag removida', message: `"${tagToRemove.label}" foi removida.` });
  }, [currentTags, clientId, database, onTagsChange, notify]);

  const handleSaveEdit = useCallback(() => {
    if (!editingTag || !customTagInput.trim()) return;

    const updatedTags = currentTags.map(t => 
      t.id === editingTag.id ? { ...t, label: customTagInput.trim() } : t
    );

    setCurrentTags(updatedTags);
    if (onTagsChange) onTagsChange(updatedTags);

    setEditingTag(null);
    setCustomTagInput('');
    notify({ type: 'success', title: 'Tag editada', message: 'Tag atualizada com sucesso.' });
  }, [editingTag, customTagInput, currentTags, onTagsChange, notify]);

  const handleCustomTagAdd = useCallback(() => {
    if (!customTagInput.trim()) return;
    
    const newTagLabel = customTagInput.trim();
    const exists = currentTags.some(t => t.label?.toLowerCase() === newTagLabel.toLowerCase());
    
    if (exists) {
      notify({ type: 'warning', title: 'Tag já existe', message: 'Esta tag já foi adicionada.' });
      return;
    }

    handleAddTag(newTagLabel);
  }, [customTagInput, currentTags, handleAddTag, notify]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (customTagInput) handleCustomTagAdd();
    }
  }, [customTagInput, handleCustomTagAdd]);

  const handleEditTag = useCallback((tag) => {
    setEditingTag(tag);
    setCustomTagInput(tag.label);
    setTimeout(() => customInputRef.current?.focus(), 0);
  }, []);

  // ==========================================================
  // TAMANHOS - ADAPTADOS
  // ==========================================================
  const sizes = useMemo(() => ({
    sm: { 
      tag: isMobile ? 'px-1.5 py-0.5 text-[9px]' : 'px-2 py-0.5 text-xs', 
      icon: isMobile ? 'w-2.5 h-2.5' : 'w-3 h-3', 
      input: isMobile ? 'text-[9px] py-1.5' : 'text-xs py-1', 
      button: isMobile ? 'p-1' : 'p-1' 
    },
    md: { 
      tag: isMobile ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1 text-sm', 
      icon: isMobile ? 'w-3 h-3' : 'w-3.5 h-3.5', 
      input: isMobile ? 'text-xs py-2' : 'text-sm py-1.5', 
      button: isMobile ? 'p-1.5' : 'p-1.5' 
    },
    lg: { 
      tag: isMobile ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-1.5 text-base', 
      icon: isMobile ? 'w-3.5 h-3.5' : 'w-4 h-4', 
      input: isMobile ? 'text-sm py-2' : 'text-base py-2', 
      button: isMobile ? 'p-2' : 'p-2' 
    }
  }), [isMobile]);

  const currentSize = sizes[size] || sizes.md;

  // ==========================================================
  // RENDER - ADAPTADO
  // ==========================================================
  return (
    <div className={`w-full ${className}`}>
      
      {/* Header - ADAPTADO */}
      <div className={`flex items-center justify-between ${isMobile ? 'mb-2' : 'mb-3'}`}>
        <div className="flex items-center gap-1">
          <div className={`${isMobile ? 'p-1' : 'p-1.5'} bg-purple-100 rounded-lg`}>
            <Tag className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} text-purple-600`} />
          </div>
          <h3 className={`font-medium text-slate-700 ${isMobile ? 'text-xs' : 'text-sm'}`}>
            Tags
          </h3>
          {currentTags.length > 0 && (
            <span className={`text-slate-500 ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
              ({currentTags.length}/{maxTags})
            </span>
          )}
        </div>

        {showCategories && !isMobile && (
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white"
          >
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tags actuales - ADAPTADAS */}
      {currentTags.length > 0 && (
        <div className={`flex flex-wrap gap-1 ${isMobile ? 'mb-3' : 'mb-4'}`}>
          {currentTags.map((tag) => {
            const colors = getTagColor(tag);
            return (
              <div
                key={tag.id || tag.label}
                className={`inline-flex items-center gap-0.5 ${colors.bg} ${colors.text} ${currentSize.tag} rounded-full border ${colors.border} group`}
              >
                <span className="flex items-center gap-0.5">
                  {React.cloneElement(getTagIcon(tag), { className: currentSize.icon })}
                  {tag.label}
                </span>
                {!readOnly && (
                  <div className="flex items-center gap-0.5">
                    {tag.custom && (
                      <button
                        onClick={() => handleEditTag(tag)}
                        className={`rounded-full hover:bg-white/50 transition-colors ${currentSize.button}`}
                        title="Editar tag"
                      >
                        <Edit2 className={currentSize.icon} />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className={`rounded-full hover:bg-white/50 transition-colors ${currentSize.button}`}
                      title="Remover tag"
                    >
                      <X className={currentSize.icon} />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Sugerencias - ADAPTADAS */}
      {showSuggestions && suggestions.length > 0 && (
        <div className={`${isMobile ? 'mb-3' : 'mb-4'}`}>
          <div className={`flex items-center gap-1 ${isMobile ? 'mb-1' : 'mb-2'}`}>
            <Sparkles className={`${isMobile ? 'w-2.5 h-2.5' : 'w-3 h-3'} text-purple-500`} />
            <span className={`font-medium text-purple-600 ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
              {isMobile ? 'Sugestões' : 'Sugestões para este cliente'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1">
            {suggestions.map((tag) => {
              if (!tag) return null;
              const colors = getTagColor(tag);
              return (
                <button
                  key={tag.id}
                  onClick={() => handleAddTag(tag)}
                  className={`inline-flex items-center gap-0.5 ${colors.bg} ${colors.text} ${currentSize.tag} rounded-full border ${colors.border} ${colors.hover} transition-colors opacity-70 hover:opacity-100 touch:active:scale-95`}
                  title={`Adicionar tag "${tag.label}"`}
                >
                  {React.cloneElement(getTagIcon(tag), { className: currentSize.icon })}
                  {isMobile && tag.label.length > 8 ? tag.label.substring(0,6)+'...' : tag.label}
                  <Plus className={currentSize.icon} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Búsqueda y tags disponibles - ADAPTADAS */}
      {(showSearch || !readOnly) && (
        <div>
          {showSearch && (
            <div className={`relative ${isMobile ? 'mb-2' : 'mb-3'}`}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={isMobile ? "Buscar tags..." : "Buscar tags..."}
                className={`w-full pl-7 pr-7 border border-slate-200 rounded-lg ${currentSize.input} focus:ring-2 focus:ring-purple-200`}
              />
              <Tag className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${currentSize.icon} text-slate-400`} />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2"
                >
                  <X className={`${currentSize.icon} text-slate-400 hover:text-slate-600`} />
                </button>
              )}
            </div>
          )}

          {filteredPredefinedTags.length > 0 && (
            <div className={`${isMobile ? 'mb-2' : 'mb-3'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-slate-500 ${isMobile ? 'text-[8px]' : 'text-xs'}`}>
                  {filteredPredefinedTags.length} tags disponíveis
                </span>
                {filteredPredefinedTags.length > (isMobile ? 6 : 10) && (
                  <button
                    onClick={() => setShowAllTags(!showAllTags)}
                    className={`text-purple-600 hover:text-purple-700 flex items-center gap-0.5 ${
                      isMobile ? 'text-[8px]' : 'text-xs'
                    }`}
                  >
                    {showAllTags ? 'Ver menos' : 'Ver todas'}
                    {showAllTags ? <ChevronUp className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'}`} /> : <ChevronDown className={`${isMobile ? 'w-2 h-2' : 'w-3 h-3'}`} />}
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {(showAllTags ? filteredPredefinedTags : filteredPredefinedTags.slice(0, isMobile ? 6 : 10)).map((tag) => {
                  const colors = getTagColor(tag);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => handleAddTag(tag)}
                      disabled={readOnly}
                      className={`inline-flex items-center gap-0.5 ${colors.bg} ${colors.text} ${currentSize.tag} rounded-full border ${colors.border} ${colors.hover} transition-colors ${readOnly ? 'opacity-50 cursor-not-allowed' : ''} touch:active:scale-95`}
                      title={`Adicionar tag "${tag.label}"`}
                    >
                      {React.cloneElement(getTagIcon(tag), { className: currentSize.icon })}
                      {isMobile && tag.label.length > 8 ? tag.label.substring(0,6)+'...' : tag.label}
                      {!readOnly && <Plus className={currentSize.icon} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {allowCustom && !readOnly && (
            <div className="relative">
              <input
                ref={customInputRef}
                type="text"
                value={customTagInput}
                onChange={(e) => setCustomTagInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={editingTag ? (isMobile ? 'Editar...' : 'Editar tag...') : (isMobile ? 'Nova tag...' : 'Nova tag personalizada...')}
                className={`w-full pl-7 pr-7 border border-slate-200 rounded-lg ${currentSize.input} focus:ring-2 focus:ring-purple-200`}
              />
              <Tag className={`absolute left-2 top-1/2 transform -translate-y-1/2 ${currentSize.icon} text-slate-400`} />
              {customTagInput && (
                <button
                  onClick={editingTag ? handleSaveEdit : handleCustomTagAdd}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-purple-600 hover:text-purple-700"
                  title={editingTag ? 'Salvar edição' : 'Adicionar tag'}
                >
                  {editingTag ? <Save className={currentSize.icon} /> : <Plus className={currentSize.icon} />}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mensaje informativo - ADAPTADO */}
      {currentTags.length === 0 && !readOnly && (
        <p className={`text-slate-400 mt-2 italic ${isMobile ? 'text-[9px]' : 'text-xs'}`}>
          💡 Adicione tags para organizar seus clientes
        </p>
      )}
    </div>
  );
};

// ==========================================================
// COMPONENTES AUXILIARES - ADAPTADOS
// ==========================================================
SmartTags.Display = ({ tags = [], size = 'sm', className = '' }) => {
  const isMobile = useIsMobile();
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {tags.map((tag) => {
        const tagObj = typeof tag === 'string' ? { id: tag, label: tag, category: 'personalizada' } : tag;
        const colors = getTagColor(tagObj);
        return (
          <span
            key={tagObj.id || tagObj.label}
            className={`inline-flex items-center gap-1 ${colors.bg} ${colors.text} rounded-full border ${colors.border} ${
              isMobile ? 'px-1.5 py-0.5 text-[8px]' : 'px-2 py-0.5 text-xs'
            }`}
          >
            {React.cloneElement(getTagIcon(tagObj), { className: isMobile ? 'w-2 h-2' : 'w-3 h-3' })}
            {tagObj.label}
          </span>
        );
      })}
    </div>
  );
};

SmartTags.Selector = ({ selected = [], onSelect, max = 10, ...props }) => {
  const [localSelected, setLocalSelected] = useState(selected);

  const handleToggle = useCallback((tag) => {
    setLocalSelected(prev => {
      let newSelected;
      if (prev.some(t => t.id === tag.id)) {
        newSelected = prev.filter(t => t.id !== tag.id);
      } else {
        if (prev.length >= max) {
          alert(`Máximo de ${max} tags permitidas`);
          return prev;
        }
        newSelected = [...prev, tag];
      }
      onSelect?.(newSelected);
      return newSelected;
    });
  }, [max, onSelect]);

  return (
    <SmartTags
      tags={localSelected}
      onTagsChange={onSelect}
      readOnly={false}
      {...props}
    />
  );
};

export default SmartTags;